import { useEffect, useState } from "react";
import { Modal, Button, Input, Select, InputNumber, message, DatePicker } from "antd";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ApiResponse } from "../../../models/response";
import { apiCall } from "../../../helpers/apiHelper";
// import { ApiResponse } from "../../models/response";
// import { apiCall } from "../../helpers/apiHelper";
// import { useTranslation } from "react-i18next";

//apis
import * as SmsPanelApi from '../../../apis/smsPanelApi';
import Loader from "../../shared/loader";
import { SmsAccountAdminBankModel } from "../../../models/smsAccountAdminBank";
import moment from "moment";
import useTranslation from "../../../hooks/TranslationContext";

// defines
type Props = {
  open: boolean;
  closeOnSave?: boolean;
  onSave: () => void;
  onCancel: () => void;
  amount?: any;
};

const { Option } = Select;

export default function PaymentPayingInSlipModal({ open, closeOnSave = false, amount, onSave, onCancel }: Props) {
  const { t } = useTranslation();

  const schema = yup.object().shape({
    bank: yup.string().required(t("bank-is-required")),
    account: yup.string(),
    reference: yup.string().required(t("reference-number-is-required")),
    cardNo: yup
      .string()
      .required(t("last-4-digits-required"))
      .matches(/^\d{4}$/, t("must-be-exactly-4-digits")),
    amount: yup.number().required(t("amount-is-required")).min(1, t("must-be-greater-than-0")),
    date: yup.string().required(t("payment-date-is-required")),
    name: yup.string(),
    description: yup.string(),
  });
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: amount ? { amount } : {},
  });

  const [loading, setLoading] = useState(false);
  const [banksLoading, setBanksLoading] = useState(false);
  const [banks, setBanks] = useState<SmsAccountAdminBankModel[]>([]);
  const [bankDescription, setBankDescription] = useState<string>();

  const abortController = new AbortController();

  useEffect(() => {
    getAdminBanksAsync();
  }, [open])

  useEffect(() => {
    reset(amount ? { amount } : {});
  }, [amount, reset]);

  const getAdminBanks = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(SmsPanelApi.getAdminBanks(abortSignal));

    return response.success ? SmsAccountAdminBankModel.toArrayOfClass((response.data?.value || [])) : [];
  };

  const getAdminBanksAsync = async () => {
    setBanksLoading(true);
    setBanks(await getAdminBanks(abortController.signal));
    setBanksLoading(false);
  }

  const onBankSelect = (value: any, option: any) => {
    var selectedBank = banks.find(x => x.id === value);
    if (selectedBank) {
      setValue("account", selectedBank.accountNumber);
      setBankDescription(selectedBank.description)
    }
  }

  const onSubmit = async (formData: any) => {
    setLoading(true);

    const data = {
      ...formData,
      date: formData.date ? moment(formData.date).format("YYYY-MM-DDTHH:mm:ss") : null,
      mode: "sms"
    }

    let response: ApiResponse = await apiCall(SmsPanelApi.insertPayingInSlip(data, abortController.signal));

    if (response.success) {
      message.success(t("paying-in-slip-created-successfully"));

      reset();
      if (onSave) onSave();

      if (closeOnSave) {
        onCancel();
      }

    } else
      message.error(response.error?.value);

    setLoading(false);
  };

  return (
    <Modal
      title={t("submit-paying-in-slip")}
      open={open}
      onCancel={onCancel}
      footer={null}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium">{t("bank")}</label>
          <Controller
            name="bank"
            control={control}
            render={({ field }) => (
              <Select {...field} className="w-full" placeholder={t("select-bank")} loading={banksLoading} onSelect={onBankSelect}>
                {
                  banks.map(bank => <Option value={bank.id}>{bank.name}{bank.description ? `(${bank.accountNumber})` : ""}</Option>)
                }
              </Select>
            )}
          />
          {errors.bank && errors.bank.message && <p className="text-red-500 text-sm">{errors.bank.message?.toString()}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium">{t("account")}</label>
          <Controller
            name="account"
            control={control}
            render={({ field }) => <Input {...field} placeholder={t("account")} readOnly />}
          />
          {bankDescription && <p className="text-sm">{bankDescription.toString()}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium">{t("reference-number")}</label>
          <Controller
            name="reference"
            control={control}
            render={({ field }) => <Input {...field} placeholder={t("enter-reference-number")} />}
          />
          {errors.reference && errors.reference.message && <p className="text-red-500 text-sm">{errors.reference.message?.toString()}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium">{t("last-4-digits-of-card")}</label>
          <Controller
            name="cardNo"
            control={control}
            render={({ field }) => <Input {...field} placeholder={t("enter-last-4-digits")} />}
          />
          {errors.cardNo && errors.cardNo.message && <p className="text-red-500 text-sm">{errors.cardNo.message?.toString()}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium">{t("amount")}</label>
          <Controller
            name="amount"
            control={control}
            render={({ field }) => <InputNumber {...field} className="w-full" placeholder={t("enter-amount")} />}
          />
          {errors.amount && errors.amount.message && <p className="text-red-500 text-sm">{errors.amount.message?.toString()}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium">{t("payment-date")}</label>
          <Controller
            name="date"
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                format="YYYY/MM/DD"
                className="w-full"
                placeholder={t("select-payment-date")}
              />
            )}
          />
          {errors.date && errors.date.message && <p className="text-red-500 text-sm">{errors.date.message?.toString()}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium">{t("name")}</label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => <Input {...field} placeholder={t("enter-name")} />}
          />
        </div>
        <div>
          <label className="block text-sm font-medium">{t("description")}</label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => <Input.TextArea {...field} placeholder={t("enter-description")} />}
          />
        </div>
        {!!loading && <Loader />}
        <div className="flex justify-end space-x-2 gap-2">
          <Button onClick={onCancel}>{t("cancel")}</Button>
          <Button type="primary" htmlType="submit">
            {t("submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
